import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { ModalController, IonContent } from '@ionic/angular';
import { firstValueFrom, from } from 'rxjs';
import { DbExercise } from 'src/models/db-exercises.model';
import { DbExercisesService } from 'src/services/db-exercises.service';
import { ExerciseFromDbDetailController } from '../exercise-from-db-detail/exercise-from-db-detail.component.controller';
import { ActivatedRoute, Router } from '@angular/router';
import { ExerciseFromDbController } from './exercise-from-db.component.controller';

@Component({
    selector: 'app-exercise-from-db',
    templateUrl: './exercise-from-db.component.html',
    styleUrls: ['./exercise-from-db.component.scss'],
    standalone: false
})
export class ExerciseFromDbComponent implements OnInit {
  @ViewChild(IonContent, { static: false }) content!: IonContent;

  exercises: DbExercise[] = [];
  filteredExercises: DbExercise[] = [];
  currentPage = 0;
  pageSize = 20;
  filters: any = {};
  selectedExercises: Set<string> = new Set();

  selectedForce: string = 'Select Force';
  selectedLevel: string = 'Select Level';
  selectedMechanic: string = 'Select Mechanic';
  selectedEquipment: string = 'Select Equipment';
  selectedCategory: string = 'Select Category';

  @Output() exerciseAdded = new EventEmitter<string>();

  isPage = false;

  constructor(
    private modalController: ModalController,
    private dbExercisesService: DbExercisesService,
    private detailController: ExerciseFromDbDetailController,
    private dbController: ExerciseFromDbController,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadExercises();
    this.route.url.subscribe(async (urlSegments) => {
      const isOnExercisesPreview =
        urlSegments.length > 0 && urlSegments[0].path === 'exercises-preview';

      if (isOnExercisesPreview) {
        this.isPage = true;
        await this.dbExercisesService.fetchAndDecompressData();
        this.loadExercises();
      }
    });
  }

  ngAfterViewInit() {
    this.addScrollListener();
  }

  addScrollListener() {
    if (this.content) {
      this.content.scrollEvents = true;
      this.content.ionScroll.subscribe(() => {
        this.dismissKeyboard();
      });
    }
  }

  dismissKeyboard() {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  showDetail(detail: DbExercise) {
    if (!this.isPage) {
      this.detailController.presentModal(detail);
    } else {
      this.router.navigate(['/home/exercises-preview', detail.id]);
    }
  }

  async loadExercises() {
    this.applyFilters();
  }

  cleanFilters() {
    Object.keys(this.filters).forEach((key) => {
      if (this.filters[key] === '' || this.filters[key].length === 0) {
        delete this.filters[key];
      }
    });
  }

  applyFilters() {
    this.cleanFilters();
    if (this.filters) {
      this.filteredExercises = this.dbExercisesService
        .filterExercises(this.filters)
        .slice(0, this.pageSize * (this.currentPage + 1));
    } else {
      this.loadExercises();
    }
  }

  loadMore(event: any) {
    this.currentPage++;
    const totalItems = this.dbExercisesService.filterExercises(
      this.filters
    ).length;
    const newFilteredExercises = this.dbExercisesService
      .filterExercises(this.filters)
      .slice(0, this.pageSize * (this.currentPage + 1));

    if (newFilteredExercises.length < totalItems) {
      this.filteredExercises = newFilteredExercises;
    }
    event.target.complete();
  }

  selectFilter(option: string, value: string | string[]) {
    if (Array.isArray(this.filters[option])) {
      const index = this.filters[option].indexOf(value);
      if (index > -1) {
        this.filters[option].splice(index, 1);
      } else {
        this.filters[option].push(value);
      }
    } else {
      this.filters[option] = [value];
    }
    this.currentPage = 0;
    this.applyFilters();
  }

  close() {
    this.modalController.dismiss();
  }

  getAllForces(): string[] {
    return Array.from(this.dbExercisesService.filterCategories.force);
  }

  getAllLevels(): string[] {
    return Array.from(this.dbExercisesService.filterCategories.level);
  }

  getAllMechanics(): string[] {
    return Array.from(this.dbExercisesService.filterCategories.mechanic);
  }

  getAllEquipment(): string[] {
    return Array.from(this.dbExercisesService.filterCategories.equipment);
  }

  getAllPrimaryMuscles(): string[] {
    return Array.from(this.dbExercisesService.filterCategories.primaryMuscles);
  }

  getAllSecondaryMuscles(): string[] {
    return Array.from(
      this.dbExercisesService.filterCategories.secondaryMuscles
    );
  }

  getAllCategories(): string[] {
    return Array.from(this.dbExercisesService.filterCategories.category);
  }

  async addExercise(dbExercise: DbExercise) {
    const exerciseId = await firstValueFrom(
      from(this.dbController.addExercise(dbExercise))
    );
    if (exerciseId) this.exerciseAdded.emit(exerciseId);
  }

  updateSelectedExercises(
    exerciseId: string | undefined,
    isChecked: boolean
  ): void {
    if (isChecked) {
      if (exerciseId) this.selectedExercises.add(exerciseId);
    } else {
      if (exerciseId) this.selectedExercises.delete(exerciseId);
    }
  }

  emitSelectedExercises(): void {
    const selected = Array.from(this.selectedExercises);
    let exercises: DbExercise[] = [];

    selected.forEach(async (id) => {
      const exercise = this.dbExercisesService.getExerciseById(id);
      exercises.push(exercise!);
      await this.addExercise(exercise!);
    });
    this.modalController.dismiss();
  }

  searchExercises(event: any) {
    const query = event.detail.value;
    if (query && query.trim().length >= 3) {
      this.filters.name = query.trim();
    } else {
      delete this.filters.name;
    }
    this.currentPage = 0;
    this.applyFilters();
  }
}
